<template>
  <div class="organization-view pa-10 pt-0" v-if="organization">

    <tab-navigation
        :title="$t('organization.organization')"
        icon="ico-organization-menu"
        :breadcrumbs="currentBreadcrumbs"
    >
      <v-tabs ref="orgTabs" :value="tabValue" class="org-detail-tabs">
        <!-- Dashboard (always in DOM to keep indexes stable) -->
        <v-tab :class="{ 'd-none': !isDashboardVisible }" :disabled="!isDashboardVisible" @click="$router.push({name: 'organizations.dashboard'}).catch(()=>{})">Dashboard</v-tab>

        <!-- Informations -->
        <v-tab @click="$router.push({name: 'organizations.detail'}).catch(()=>{})">{{ $t('information') }}</v-tab>

        <!-- Infrastructure -->
        <v-menu :value="activeMenu === 'infrastructure'" offset-y bottom nudge-bottom="2" :close-on-content-click="false" :close-on-click="false" content-class="org-tab-dropdown">
          <template #activator="{ attrs }">
            <v-tab v-bind="attrs" event="" @mouseenter.native="openMenu('infrastructure')" @click.native.stop="clickTab('infrastructure')" @mouseleave.native="scheduleClose">
              {{ $t('equipments') }} <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
            </v-tab>
          </template>
          <v-list @mouseenter.native="cancelClose" @mouseleave.native="closeMenu">
            <v-list-item :to="{name: 'organizations.charging-points'}" @click="closeMenu">
              <v-list-item-title>{{ $t('charging-point.charging-points') }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.meter-devices'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('meter-device.meter-device', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.locations'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('location.location', 2) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Membres -->
        <v-menu :value="activeMenu === 'members'" offset-y bottom nudge-bottom="2" :close-on-content-click="false" :close-on-click="false" content-class="org-tab-dropdown">
          <template #activator="{ attrs }">
            <v-tab v-bind="attrs" event="" @mouseenter.native="openMenu('members')" @click.native.stop="clickTab('members')" @mouseleave.native="scheduleClose">
              {{ $t('drivers') }} <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
            </v-tab>
          </template>
          <v-list @mouseenter.native="cancelClose" @mouseleave.native="closeMenu">
            <v-list-item :to="{name: 'organizations.members'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('member.member', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.customers'}" @click="closeMenu" v-if="currentUser.isZE">
              <v-list-item-title>{{ $tc('customer.customer', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.tokens'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('rfid.rfid', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.vehicles'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('vehicle.vehicle', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.members.invitations'}" @click="closeMenu">
              <v-list-item-title>{{ $t('member.invitation.manage') }}</v-list-item-title>
            </v-list-item>
            <!-- Ce que le gestionnaire a à faire sur les commandes de ses membres (valider, remettre des badges) -->
            <v-list-item v-if="currentUser.permissions.includes('ONBOARDING_MODULE_MANAGE') || currentUser.permissions.includes('COMMISSIONING_REQUEST')" :to="{name: 'organizations.pending-orders'}" @click="closeMenu">
              <v-list-item-title>{{ $t('onboarding.pending-orders.title') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Activité -->
        <v-menu :value="activeMenu === 'activity'" offset-y bottom nudge-bottom="2" :close-on-content-click="false" :close-on-click="false" content-class="org-tab-dropdown">
          <template #activator="{ attrs }">
            <v-tab v-bind="attrs" event="" @mouseenter.native="openMenu('activity')" @click.native.stop="clickTab('activity')" @mouseleave.native="scheduleClose">
              {{ $t('organization.activity') }} <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
            </v-tab>
          </template>
          <v-list @mouseenter.native="cancelClose" @mouseleave.native="closeMenu">
            <v-list-item :to="{name: 'organizations.usages'}" @click="closeMenu">
              <v-list-item-title>{{ $t('charging-point.usage') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="tabs.includes('expense')" :to="{name: 'organizations.expense-periods'}" @click="closeMenu">
              <v-list-item-title>{{ $t('expense-refund.expense-refunds') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE && tabs.includes('energy-provider')" :to="{name: 'organizations.energy-provider'}" @click="closeMenu">
              <v-list-item-title>{{ $t('recharge-service.recharge-service') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE && currentUser.permissions.includes('ORGANIZATION_WRITE')" :to="{name: 'organizations.authorizations'}" @click="closeMenu">
              <v-list-item-title>{{ $t('session-authorization.logs') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Finance -->
        <v-menu :value="activeMenu === 'finance'" offset-y bottom nudge-bottom="2" :close-on-content-click="false" :close-on-click="false" content-class="org-tab-dropdown">
          <template #activator="{ attrs }">
            <v-tab v-bind="attrs" event="" @mouseenter.native="openMenu('finance')" @click.native.stop="clickTab('finance')" @mouseleave.native="scheduleClose">
              Finance <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
            </v-tab>
          </template>
          <v-list @mouseenter.native="cancelClose" @mouseleave.native="closeMenu">
            <v-list-item v-if="currentUser.isZE" :to="{name: 'organizations.invoices'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('invoice.invoice', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE" :to="{name: 'organizations.billing-requests'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('billing-request.billing-request', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.tariffs'}" @click="closeMenu">
              <v-list-item-title>{{ $t('organization.pricing') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.permissions.includes('ORGANIZATION_WRITE')" :to="{name: 'organizations.detail.cost-centers'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('cost-center.cost-center', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE" :to="{name: 'organizations.subscriptions'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('subscription.subscription', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE && currentUser.permissions.includes('PAYMENT_MANAGE')" :to="{name: 'organizations.payment-methods'}" @click="closeMenu">
              <v-list-item-title>{{ $t('payment-method.payment-methods') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE && currentUser.permissions.includes('PAYMENT_MANAGE')" :to="{name: 'organizations.transactions'}" @click="closeMenu">
              <v-list-item-title>{{ $t('payment-method-ui.transactions') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Configuration -->
        <v-menu v-if="hasSettingsAccess" :value="activeMenu === 'settings'" offset-y bottom nudge-bottom="2" :close-on-content-click="false" :close-on-click="false" content-class="org-tab-dropdown">
          <template #activator="{ attrs }">
            <v-tab v-bind="attrs" event="" @mouseenter.native="openMenu('settings')" @click.native.stop="clickTab('settings')" @mouseleave.native="scheduleClose">
              {{ $t('configuration') }} <v-icon x-small class="ml-1">mdi-chevron-down</v-icon>
            </v-tab>
          </template>
          <v-list @mouseenter.native="cancelClose" @mouseleave.native="closeMenu">
            <v-list-item v-if="currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')" :to="{name: 'organizations.settings'}" @click="closeMenu">
              <v-list-item-title>{{ $t('dashboard-config.title') }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.members.groups'}" @click="closeMenu">
              <v-list-item-title>{{ $t('member-group.manage') }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.members.types'}" @click="closeMenu">
              <v-list-item-title>{{ $t('member-type.manage') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.permissions.includes('ONBOARDING_MODULE_MANAGE')" :to="{name: 'organizations.charging-policies'}" @click="closeMenu">
              <v-list-item-title>{{ $t('onboarding.charging-settings.title') }}</v-list-item-title>
            </v-list-item>
            <v-list-item :to="{name: 'organizations.users'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('user.user', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')" :to="{name: 'organizations.billing-info'}" @click="closeMenu">
              <v-list-item-title>{{ $t('organization.billing-info') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')" :to="{name: 'organizations.roaming-config'}" @click="closeMenu">
              <v-list-item-title>{{ $t('organization.roaming-config') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE" :to="{name: 'organizations.pdls'}" @click="closeMenu">
              <v-list-item-title>{{ $tc('pdl.pdl', 2) }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')" :to="{name: 'organizations.sso'}" @click="closeMenu">
              <v-list-item-title>SSO</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-tabs>
    </tab-navigation>

    <keep-alive :include="cachedPages">
      <router-view></router-view>
    </keep-alive>

  </div>
</template>

<script>
import OrganizationRepository from "@/repository/OrganizationRepository"
import TabNavigation from "@blocks/TabNavigation.vue";

export default {
  name: 'OrganizationDetail',
  components: {TabNavigation},
  data() {
    return {
      organization: null,
      tabs: [],
      activeMenu: null,
      closeTimer: null,
      tabValue: undefined,
    }
  },

  mounted() {
    this.loadData()
    this.loadTabs()
    this.tabValue = this.activeTabIndex
  },

  watch: {
    id(newId) {
      // This component is kept alive. When the user drills into a sibling detail page (e.g.
      // /vehicles/:id), the route param `id` becomes that entity's id — NOT an org id. Reloading
      // then would fetch the wrong entity (GET /organization/get/<vehicleId> → 403). So only react
      // when we're actually on an organization route, and skip if that org is already loaded.
      if (!this.isOrganizationRoute) return
      if (this.organization && this.organization.id === newId) return
      this.loadData()
    },

    activeTabIndex(val) {
      this.tabValue = val
    },

    isDashboardVisible() {
      // When dashboard tab appears/disappears, force slider recalculation
      this.$nextTick(() => {
        this.tabValue = this.activeTabIndex
        this.$nextTick(() => {
          if (this.$refs.orgTabs) {
            this.$refs.orgTabs.callSlider()
          }
        })
      })
    },

    '$route.name'() {
      this.tabValue = this.activeTabIndex
    },
  },

  computed: {

    id() {
      return this.$route.params.id
    },

    // True only while the current route is actually an organization route (this component's own
    // route or one of its tabs). Guards keep-alive side-effects when the user is on a sibling
    // detail page (e.g. /vehicles/:id) where `$route.params.id` is a different entity's id.
    isOrganizationRoute() {
      return this.$route.matched.some(m => (m.path || '').includes('organizations/:id'))
    },

    isDashboardVisible() {
      return this.organization?.dashboardConfig?.enabled === true
    },

    hasSettingsAccess() {
      return this.currentUser.isZE || this.currentUser.permissions.includes('ORGANIZATION_WRITE')
    },

    // Compute the numeric tab index to force v-tabs to select the correct tab
    activeTabIndex() {
      const name = this.$route.name
      let idx = 0

      // Dashboard (always index 0, tab always in DOM)
      if (name === 'organizations.dashboard') return idx
      idx++

      // Informations
      if (name === 'organizations.detail' || name === 'organizations.edit') return idx
      idx++

      // Infrastructure
      if (this.isInGroup('infrastructure')) return idx
      idx++

      // Membres
      if (this.isInGroup('members')) return idx
      idx++

      // Activité
      if (this.isInGroup('activity')) return idx
      idx++

      // Finance
      if (this.isInGroup('finance')) return idx
      idx++

      // Configuration
      if (this.hasSettingsAccess) {
        if (this.isInGroup('settings')) return idx
      }

      // No match — return undefined so no tab is highlighted
      return undefined
    },

    routeLabels() {
      return {
        'organizations.dashboard': 'Dashboard',
        'organizations.detail': this.$t('information'),
        'organizations.edit': this.$t('information'),
        'organizations.locations': this.$tc('location.location', 2),
        'organizations.charging-points': this.$t('charging-point.charging-points'),
        'organizations.meter-devices': this.$tc('meter-device.meter-device', 2),
        'organizations.meter-devices-readings': this.$tc('meter-device.meter-device', 2),
        'organizations.members': this.$tc('member.member', 2),
        'organizations.members.invitations': this.$t('member.invitation.manage'),
        'organizations.members.invitations.manage': this.$t('member.invitation.manage'),
        'organizations.members.registration': this.$t('onboarding.registration.file'),
        'organizations.charging-policies': this.$t('onboarding.charging-policy.list.title'),
        'organizations.equipment-profiles': this.$t('onboarding.equipment-profile.title'),
        'organizations.charging-policy': this.$t('onboarding.charging-policy.title'),
        'organizations.equipment-order': this.$t('onboarding.commissioning.orders'),
        'organizations.customers': this.$tc('customer.customer', 2),
        'organizations.tokens': this.$tc('rfid.rfid', 2),
        'organizations.vehicles': this.$tc('vehicle.vehicle', 2),
        'organizations.users': this.$tc('user.user', 2),
        'organizations.usages': this.$t('charging-point.usage'),
        'organizations.expense-periods': this.$t('expense-refund.expense-refunds'),
        'organizations.expense-periods.manage': this.$t('expense-refund.expense-refunds'),
        'organizations.expense-periods.member': this.$t('expense-refund.expense-refunds'),
        'organizations.energy-provider': this.$t('recharge-service.recharge-service'),
        'organizations.energy-provider.period': this.$t('recharge-service.recharge-service'),
        'organizations.energy-provider.period.lines': this.$t('recharge-service.recharge-service'),
        'organizations.authorizations': this.$t('session-authorization.logs'),
        'organizations.tariffs': this.$t('organization.pricing'),
        'organizations.detail.cost-centers': this.$tc('cost-center.cost-center', 2),
        'organizations.subscriptions': this.$tc('subscription.subscription', 2),
        'organizations.subscriptions.edit': this.$tc('subscription.subscription', 2),
        'organizations.subscriptions.licences': this.$tc('subscription.subscription', 2),
        'organizations.payment-methods': this.$t('payment-method.payment-methods'),
        'organizations.payout': this.$t('payment-method.payment-methods'),
        'organizations.transactions': this.$t('payment-method-ui.transactions'),
        'organizations.invoices': this.$tc('invoice.invoice', 2),
        'organizations.billing-requests': this.$tc('billing-request.billing-request', 2),
        'organizations.billing-info': this.$t('organization.billing-info'),
        'organizations.roaming-config': this.$t('organization.roaming-config'),
        'organizations.sso': 'SSO',
        'organizations.members.groups': this.$t('member-group.manage'),
        'organizations.members.groups.add': this.$t('member-group.manage'),
        'organizations.members.groups.edit': this.$t('member-group.manage'),
        'organizations.members.types': this.$t('member-type.manage'),
        'organizations.members.types.add': this.$t('member-type.manage'),
        'organizations.members.types.edit': this.$t('member-type.manage'),
        'organizations.settings': this.$t('dashboard-config.title'),
      }
    },

    currentPageLabel() {
      return this.routeLabels[this.$route.name] || null
    },

    currentBreadcrumbs() {
      const crumbs = [
        { text: this.$t('organization.organizations'), to: { name: 'organizations.search' }, exact: true },
        { text: this.organization.name, to: { name: 'organizations.detail', params: { id: this.id } }, exact: true },
      ]
      if (this.currentPageLabel && this.$route.name !== 'organizations.detail') {
        crumbs.push({ text: this.currentPageLabel })
      }
      return crumbs
    },

    cachedPages() {
      let pages = []

      pages.push('OrganizationVehicles')

      return pages
    },

    routeGroups() {
      return {
        infrastructure: ['organizations.locations', 'organizations.charging-points', 'organizations.meter-devices', 'organizations.meter-devices-readings'],
        members: ['organizations.members', 'organizations.customers', 'organizations.tokens', 'organizations.vehicles', 'organizations.members.invitations', 'organizations.members.invitations.manage', 'organizations.members.registration', 'organizations.pending-orders', 'organizations.equipment-order'],
        activity: ['organizations.usages', 'organizations.expense-periods', 'organizations.expense-periods.manage', 'organizations.expense-periods.member', 'organizations.energy-provider', 'organizations.energy-provider.period', 'organizations.energy-provider.period.lines', 'organizations.authorizations'],
        finance: ['organizations.invoices', 'organizations.billing-requests', 'organizations.tariffs', 'organizations.detail.cost-centers', 'organizations.subscriptions', 'organizations.subscriptions.edit', 'organizations.subscriptions.licences', 'organizations.payment-methods', 'organizations.payout', 'organizations.transactions'],
        settings: ['organizations.billing-info', 'organizations.roaming-config', 'organizations.sso', 'organizations.members.groups', 'organizations.members.groups.add', 'organizations.members.groups.edit', 'organizations.members.types', 'organizations.members.types.add', 'organizations.members.types.edit', 'organizations.users', 'organizations.settings', 'organizations.charging-policies', 'organizations.charging-policy', 'organizations.equipment-profiles'],
      }
    },
  },

  methods: {
    isInGroup(group) {
      const routes = this.routeGroups[group] || []
      return routes.includes(this.$route.name)
    },

    clickTab(group) {
      this.openMenu(group)
      // Navigate to the group's default page if not already in the group
      if (!this.isInGroup(group)) {
        const defaults = {
          infrastructure: 'organizations.charging-points',
          members: 'organizations.members',
          activity: 'organizations.usages',
          finance: 'organizations.invoices',
          settings: 'organizations.settings',
        }
        const target = defaults[group]
        if (target) {
          this.$router.push({ name: target, params: { id: this.id } }).catch(() => {})
        }
      }
    },

    openMenu(name) {
      this.cancelClose()
      this.activeMenu = name
    },

    closeMenu() {
      this.cancelClose()
      this.activeMenu = null
    },

    scheduleClose() {
      this.cancelClose()
      this.closeTimer = setTimeout(() => {
        this.activeMenu = null
      }, 150)
    },

    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },

    loadData() {
      return new Promise((resolve, reject) => {
        if(this.id) {
          this.showLoading(true)
          OrganizationRepository.get(this.id).then(organization => {
            this.organization = organization
            this.showLoading(false)
            resolve(organization)
          }).catch(err => {
            this.notifyError(err)
            this.showLoading(false)
            reject(err)
          })
        }
      })
    },

    loadTabs() {
      if(this.id) {
        OrganizationRepository.tabs(this.id).then(tabs => {
          this.tabs = tabs
        }).catch(err => {
          this.notifyError(err)
        })
      }
    },

  },

  beforeDestroy() {
    this.cancelClose()
  },

}
</script>

<style lang="scss">
.org-tab-dropdown {
  margin-top: 0 !important;

  .v-list {
    padding: 6px 0;
  }
  .v-list-item {
    min-height: 40px;
    padding: 0 20px;
  }
  .v-list-item-title {
    font-size: 14px !important;
  }
  .v-list-item--active {
    color: var(--v-primary-base);
    .v-list-item-title {
      font-weight: 600;
    }
  }
}
</style>
